import {
  isQaqcRelevantWorkorderType,
  normalizeWorkorderType,
} from './sampleTrackingWorkorderTypes';

export const COLMATCH_STATUS = {
  FAILED: 'failed',
  PENDING: 'pending',
  PENDING_CU_AA05: 'pending_cu_aa05',
  MISSING_SAMPLES: 'missing_samples',
  PASSED: 'passed',
  UNKNOWN: 'unknown',
};

const INCOMPLETE_ORIG = 'INCOMPLETE_ORIG_SAMPLES';

export function allWorkordersFinalized(workorderDetails) {
  const list = workorderDetails || [];
  if (list.length === 0) return false;
  return list.every((wo) => wo.status === 'Finalized');
}

export function hasActiveWorkorder(workorderDetails) {
  return (workorderDetails || []).some((wo) => wo.status === 'Active');
}

export function hasPendingSequentialLeach(workorderDetails) {
  return (workorderDetails || []).some((wo) => {
    const t = normalizeWorkorderType(wo.workorder_type);
    return t === 'SEQUENTIAL LEACH' && wo.status !== 'Finalized';
  });
}

/**
 * Unresolved failures on this hole limited to assay-relevant workorders.
 */
export function getAssayUnresolvedFailures(hole) {
  const failures = hole?.colmatch_unresolved_failures || [];
  const details = hole?.workorder_details || [];
  const assayWoIds = new Set(
    details
      .filter((wo) => isQaqcRelevantWorkorderType(wo.workorder_type))
      .map((wo) => wo.workorder_id)
      .filter(Boolean)
  );

  return failures.filter((f) => {
    if (!f.workorder_id) return false;
    if (assayWoIds.size > 0) return assayWoIds.has(f.workorder_id);
    const detail = details.find((d) => d.workorder_id === f.workorder_id);
    return detail && isQaqcRelevantWorkorderType(detail.workorder_type);
  });
}

function buildTooltip(hole, extraParts = []) {
  const parts = [...extraParts];
  if (hole.colmatch_passed_sample_count != null || hole.colmatch_expected_sample_count != null) {
    parts.push(
      `Samples: ${hole.colmatch_passed_sample_count ?? '—'}/${hole.colmatch_expected_sample_count ?? '—'}`
    );
  }
  if (hole.colmatch_fail_reason) {
    parts.push(hole.colmatch_fail_reason);
  }
  return parts.filter(Boolean).join(' — ');
}

/**
 * @returns {{ code: string, label: string, badgeClass: string, tooltip: string, failureLink?: object }}
 */
export function computeColmatchDisplayStatus(hole) {
  if (!hole) {
    return {
      code: COLMATCH_STATUS.UNKNOWN,
      label: 'N/A',
      badgeClass: '',
      tooltip: '',
    };
  }

  const assayFailures = getAssayUnresolvedFailures(hole);
  if (assayFailures.length > 0) {
    const primary = assayFailures[0];
    const sampleNumber = primary.sample_number || primary.dup_sample_number || '';
    return {
      code: COLMATCH_STATUS.FAILED,
      label: 'Failed',
      badgeClass: 'failed',
      tooltip: buildTooltip(hole, [
        `Unresolved QAQC failure${sampleNumber ? `: sample ${sampleNumber}` : ''}`,
        primary.failure_type,
      ]),
      failureLink: {
        failureId: primary.id,
        workorderId: primary.workorder_id,
        sampleNumber,
        holeId: primary.hole_id || hole.hole_id,
      },
    };
  }

  const details = hole.workorder_details || [];
  const fallbackUnresolved = details.some(
    (wo) =>
      isQaqcRelevantWorkorderType(wo.workorder_type) && wo.has_unresolved_failure === true
  );
  if (fallbackUnresolved && hole.colmatch_unresolved_failures_api_ok !== true) {
    return {
      code: COLMATCH_STATUS.FAILED,
      label: 'Failed',
      badgeClass: 'failed',
      tooltip: buildTooltip(hole, [
        'Unresolved failure on assay workorder (failure detail unavailable)',
      ]),
    };
  }

  if (hasActiveWorkorder(details)) {
    return {
      code: COLMATCH_STATUS.PENDING,
      label: 'PENDING',
      badgeClass: 'pending',
      tooltip: buildTooltip(hole, ['Active workorder on hole']),
    };
  }

  if (hasPendingSequentialLeach(details)) {
    return {
      code: COLMATCH_STATUS.PENDING_CU_AA05,
      label: 'PENDING Cu-AA05',
      badgeClass: 'passed',
      tooltip: buildTooltip(hole, ['Sequential leach workorder not finalized']),
    };
  }

  if (
    allWorkordersFinalized(details) &&
    hole.colmatch_fail_reason === INCOMPLETE_ORIG
  ) {
    return {
      code: COLMATCH_STATUS.MISSING_SAMPLES,
      label: 'MISSING SAMPLES',
      badgeClass: 'missing-samples',
      tooltip: buildTooltip(hole, ['Expected vs passed ORIG sample count mismatch']),
    };
  }

  if (hole.colmatch_hole_passed === true) {
    return {
      code: COLMATCH_STATUS.PASSED,
      label: 'Passed',
      badgeClass: 'passed',
      tooltip: buildTooltip(hole),
    };
  }

  if (hole.colmatch_has_data || hole.colmatch_hole_passed === false) {
    const reason = hole.colmatch_fail_reason || 'Colmatch check incomplete';
    return {
      code: COLMATCH_STATUS.UNKNOWN,
      label: 'N/A',
      badgeClass: '',
      tooltip: buildTooltip(hole, [reason]),
    };
  }

  return {
    code: COLMATCH_STATUS.UNKNOWN,
    label: 'N/A',
    badgeClass: '',
    tooltip: '',
  };
}
